/**
 * telemetry.ts —— 埋点（PRD 第九章）
 *
 * 本期不接真实上报通道：事件写入内存环形队列 + console，供 QA 与自测核对。
 * 口径要点：
 *   - `calc_result` 的 exact 恒为 true（引擎无近似路径）；
 *   - 活跃对手数（activeOpponents）**只作统计维度**，绝不进入赢率计算输入。
 */

export type TelemetryEvent =
  | 'game_start'
  | 'hand_end'
  | 'player_action'
  | 'calc_open'
  | 'calc_close'
  | 'calc_result'
  | 'calc_cancel'
  | 'calc_unavailable'
  /** 计算线程异常 / 超时兜底（QA-BUG-022：worker 启动失败时也必须有可观测出口）。 */
  | 'calc_error'
  | 'calc_slow'
  | 'replay_open'
  | 'score_shown'
  | 'speed_change'
  /** R13-1：随时弃牌（是否越轮、街）。 */
  | 'fold_anytime'
  /** Round21 T6 / AC-S1：取消尚未落地的越轮弃牌预选。 */
  | 'fold_anytime_cancel'
  /** R13-2：弃牌后推进模式（turbo/normal、是否来自记忆）。 */
  | 'fold_advance_mode'
  | 'save_resume';

export interface TelemetryRecord {
  readonly seq: number;
  readonly ts: number;
  readonly event: TelemetryEvent;
  readonly props: Readonly<Record<string, unknown>>;
}

const BUFFER_LIMIT = 500;
const buffer: TelemetryRecord[] = [];
let seq = 0;
let debugLog = false;

/** 打一个点。返回落库后的记录，便于单测断言。 */
export function track(event: TelemetryEvent, props: Record<string, unknown> = {}): TelemetryRecord {
  seq += 1;
  const rec: TelemetryRecord = { seq, ts: Date.now(), event, props: { ...props } };
  buffer.push(rec);
  if (buffer.length > BUFFER_LIMIT) buffer.splice(0, buffer.length - BUFFER_LIMIT);
  if (debugLog && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.debug(`[telemetry] ${event}`, rec.props);
  }
  return rec;
}

export function telemetryBuffer(): readonly TelemetryRecord[] {
  return buffer;
}

export function clearTelemetry(): void {
  buffer.length = 0;
  seq = 0;
}

export function setTelemetryDebug(on: boolean): void {
  debugLog = on;
}

/** 导出为 JSON 文本（QA 取证用）。 */
export function telemetryToJson(): string {
  return JSON.stringify(buffer, null, 2);
}

/** 浏览器里挂到 window，方便 QA / 自测在控制台核对事件流。 */
export function exposeTelemetry(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, unknown>).__riverside = {
    telemetry: telemetryBuffer,
    telemetryJson: telemetryToJson,
    clearTelemetry,
  };
}
